'use client'

import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Box, Typography, Avatar, Grid, Card, CardContent, IconButton, Tooltip, Chip, Divider, Drawer } from '@mui/material';
import { useLocation, useNavigate } from 'react-router-dom';
import { createColumnHelper } from '@tanstack/react-table';
import { Icon } from '@iconify/react';
import Swal from 'sweetalert2';

import { getSession } from '../../../../getSession';
import { getEventDetailList, getActivityDetailById } from '../../../../utils/lead';
import PremiumModuleWrapper from '../../../Task/Components/common/PremiumModuleWrapper';
import SubHeader from '../../../../components/common/SubHeader/SubHeader';
import GridMainPanelLayout from '../../../../components/common/Panels/GridMainPanelLayout';
import { useModulePermissions } from '../../../../hooks/useModulePermissions';

const columnHelper = createColumnHelper();

const WebhookEventsWrapper = () => {
    const [data, setData] = useState([]);
    const [loading, setLoading] = useState(true);
    const [totalRecords, setTotalRecords] = useState(0);
    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage, setItemsPerPage] = useState(20);
    const [searchTerm, setSearchTerm] = useState('');
    const [hasFetched, setHasFetched] = useState(false);
    const [viewMode, setViewMode] = useState('table');
    const [selectedItem, setSelectedItem] = useState(null);
    const [stats, setStats] = useState({
        total: 0,
        success: 0,
        pending: 0,
        failed: 0
    });

    const [isDrawerOpen, setIsDrawerOpen] = useState(false);
    const [drawerJsonStr, setDrawerJsonStr] = useState('');
    const [drawerMode, setDrawerMode] = useState('view');
    const [selectedRequestId, setSelectedRequestId] = useState(null);
    const [editFields, setEditFields] = useState({});

    const location = useLocation();
    const navigate = useNavigate();
    const queryParams = new URLSearchParams(location.search);
    const { userId, parentId, TokenId } = getSession();
    const { canAdd, canEdit, canDelete, canView, canSearch, PermissionDeniedComponent } = useModulePermissions('PAG10332');

    // Get ActivityId from query params or state
    const ActivityId = useMemo(() => {
        return queryParams.get('ActivityId') || location.state?.ActivityId || '';
    }, [location]);

    const fetchEvents = useCallback(async () => {
        setLoading(true);
        try {
            const payload = {
                UserId: userId,
                token: TokenId,
                FilterText: canSearch ? searchTerm : '',
                ActivityId: ActivityId,
                draw: 1,
                start: (currentPage - 1) * itemsPerPage,
                length: itemsPerPage
            };

            const response = await getEventDetailList(payload);

            let result = response.d || response;
            if (typeof result === 'string') {
                try { result = JSON.parse(result); } catch (e) { }
            }

            const events = result.data || result.Details || [];
            setData(events);
            const total = result.recordsTotal || events.length;
            setTotalRecords(total);

            const successCount = result.successRecords || result.SuccessCount || events.filter(e => e.Status?.toLowerCase() === 'success').length;
            const pendingCount = result.pendingRecords || result.PendingCount || events.filter(e => e.Status?.toLowerCase() === 'pending').length;
            const failedCount = result.failedRecords || result.FailedCount || events.filter(e => e.Status?.toLowerCase() === 'failure' || e.Status?.toLowerCase() === 'failed').length;

            setStats({
                total: total,
                success: successCount,
                pending: pendingCount,
                failed: failedCount
            });

            setHasFetched(true);

            if (events.length > 0 && !selectedItem) {
                setSelectedItem(events[0]);
            }
        } catch (error) {
            console.error('Error fetching webhook events:', error);
            if (!hasFetched) setData([]);
        } finally {
            setLoading(false);
        }
    }, [userId, TokenId, searchTerm, ActivityId, currentPage, itemsPerPage, hasFetched]);

    useEffect(() => {
        fetchEvents();
    }, [fetchEvents]);

    const handleViewRequestJson = (jsonStr) => {
        setDrawerJsonStr(jsonStr);
        setDrawerMode('view');
        setIsDrawerOpen(true);
    };

    const handleShowAll = () => {
        setSearchTerm('');
        setCurrentPage(1);
        setSelectedItem(null);
        navigate('/custom-events/webhook', { replace: true });
    };

    const handleGenerateEvent = (requestId, jsonStr) => {
        let parsed = {};
        try {
            parsed = JSON.parse(jsonStr || '{}');
        } catch (e) {
            console.error('Error parsing JSON string:', e);
        }

        // Initialize editFields state with parsed properties
        const initialFields = {};
        Object.entries(parsed).forEach(([key, value]) => {
            if (key !== 'EventIdentifier' && key !== 'EntityIdentifier') {
                initialFields[key] = Array.isArray(value)
                    ? value.join(',')
                    : typeof value === 'object' && value !== null
                        ? JSON.stringify(value)
                        : String(value);
            }
        });

        setEditFields(initialFields);
        setSelectedRequestId(requestId);
        setDrawerJsonStr(jsonStr);
        setDrawerMode('edit');
        setIsDrawerOpen(true);
    };

    const handleCopyValue = (key, val) => {
        setEditFields(prev => ({
            ...prev,
            [key]: val
        }));
    };

    const handleEditFieldChange = (key, val) => {
        setEditFields(prev => ({
            ...prev,
            [key]: val
        }));
    };

    const handleSaveEvent = async () => {
        let errors = [];
        const requestObject = {};

        let parsed = {};
        try {
            parsed = JSON.parse(drawerJsonStr || '{}');
        } catch (e) {
            console.error('Error parsing event JSON:', e);
        }

        const visibleKeys = Object.keys(parsed).filter(key => {
            return key !== 'EventIdentifier' && key !== 'EntityIdentifier';
        });

        visibleKeys.forEach(key => {
            const val = editFields[key];
            requestObject[key] = val;
            if (val === undefined || val === null || String(val).trim() === '') {
                errors.push(`Please provide value for ${key}.`);
            }
        });

        // Add back hidden properties to requestObject to ensure complete payload integrity
        Object.keys(parsed).forEach(key => {
            if (key === 'EventIdentifier' || key === 'EntityIdentifier') {
                requestObject[key] = parsed[key];
            }
        });

        if (errors.length > 0) {
            Swal.fire({
                title: 'Oops',
                html: errors.join('<br />'),
                icon: 'warning'
            });
            return;
        }

        try {
            const numericUserId = parseFloat(userId) || 0;
            const payload = {
                Token: TokenId || '',
                Message: '',
                LoggedUserId: numericUserId,
                MAC_Address: '',
                IP_Address: '',
                Details: {
                    RequestId: selectedRequestId,
                    ParentId: parseFloat(parentId) || 0,
                    RequestJSON: JSON.stringify(requestObject)
                },
                BroadcastName: ''
            };

            const response = await getActivityDetailById(payload);

            if (response && response.Details) {
                setIsDrawerOpen(false);
                Swal.fire('Success', 'Data saved successfully', 'success');
                fetchEvents(); // Reload the table data
            } else {
                Swal.fire('Oops', 'Unable to process your request, please contact the administrator or support team.', 'warning');
            }
        } catch (error) {
            console.error('Error saving event:', error);
            Swal.fire('Oops', 'Unable to process your request, please contact the administrator or support team.', 'warning');
        }
    };

    const columns = useMemo(() => [
        columnHelper.accessor('CreatedOn', {
            header: 'Created On',
            cell: info => (
                <Typography variant="body2" sx={{ color: '#475569', fontSize: '13px' }}>
                    {info.getValue()}
                </Typography>
            )
        }),
        columnHelper.accessor('RequestJson', {
            header: 'Request Json',
            cell: info => {
                const val = info.getValue();
                if (!val) return '-';
                return (
                    <Box>
                        <Typography variant="body2" sx={{
                            color: '#64748b',
                            fontSize: '13px',
                            maxWidth: '200px',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap'
                        }}>
                            {val.substring(0, 50)}...
                        </Typography>
                        <Typography
                            variant="caption"
                            sx={{ color: '#3b82f6', cursor: 'pointer', fontWeight: 600, '&:hover': { textDecoration: 'underline' } }}
                            onClick={() => handleViewRequestJson(val)}
                        >
                            View
                        </Typography>
                    </Box>
                );
            }
        }),
        columnHelper.accessor('EntityProfile', {
            header: 'Entity Profile',
            cell: info => {
                const val = info.getValue();
                if (!val) {
                    return (
                        <Box sx={{ bgcolor: '#fee2e2', color: '#ef4444', p: 1.5, borderRadius: '8px', textAlign: 'center', fontSize: '12px', fontWeight: 700, width: '140px' }}>
                            Entity Profile Not Found
                        </Box>
                    );
                }
                try {
                    const profile = JSON.parse(val);
                    return (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                            <Avatar src="/assets/custom/img/img_avatar1.png" sx={{ width: 40, height: 40 }} />
                            <Box>
                                <Typography variant="body2" sx={{ fontWeight: 700, color: '#1e293b', fontSize: '13px' }}>
                                    {profile.PersonName}
                                </Typography>
                                <Typography variant="caption" sx={{ display: 'block', color: '#64748b' }}>
                                    Entity Type: <Box component="span" sx={{ fontWeight: 600 }}>{profile.EntiyName}</Box>
                                </Typography>
                                <Typography variant="caption" sx={{ display: 'block', color: '#64748b' }}>
                                    Entity No: <Box component="span" sx={{ fontWeight: 600 }}>{profile.EntiyName?.toLowerCase() === 'lead' ? profile.LeadNo : profile.EntityId}</Box>
                                </Typography>
                            </Box>
                        </Box>
                    );
                } catch (e) {
                    return <Typography variant="body2" color="error">Error parsing profile</Typography>;
                }
            }
        }),
        columnHelper.accessor('Status', {
            header: 'Status',
            cell: info => {
                const status = info.getValue();
                const isSuccess = status?.toLowerCase() === 'success';
                return (
                    <Box sx={{
                        bgcolor: isSuccess ? '#22c55e' : '#f59e0b',
                        color: 'white', px: 2, py: 1, borderRadius: '6px', textAlign: 'center', fontSize: '12px', fontWeight: 600, width: '100px', textTransform: 'capitalize'
                    }}>
                        {status}
                    </Box>
                );
            }
        }),
        columnHelper.display({
            id: 'eventDetails',
            header: 'Event Details',
            cell: info => {
                const row = info.row.original;
                if (row.Status?.toLowerCase() === 'success') {
                    return (
                        <Typography variant="body2" sx={{ color: '#475569', fontSize: '13px' }}>
                            {row.EventDescription}
                        </Typography>
                    );
                }
                if (!canEdit && !canAdd) {
                    return (
                        <Typography variant="body2" sx={{ color: '#64748b', fontSize: '13px', fontStyle: 'italic' }}>
                            Pending generation
                        </Typography>
                    );
                }
                return (
                    <Typography
                        variant="body2"
                        sx={{ color: '#3b82f6', cursor: 'pointer', fontWeight: 600, '&:hover': { textDecoration: 'underline' } }}
                        onClick={() => handleGenerateEvent(row.Id, row.RequestJson)}
                    >
                        Generate Event
                    </Typography>
                );
            }
        }),
        columnHelper.accessor('ErrorDescription', {
            header: 'Error Description',
            cell: info => (
                <Typography variant="body2" sx={{ color: '#ef4444', fontSize: '13px', maxWidth: '200px' }}>
                    {info.getValue()}
                </Typography>
            )
        })
    ], [canEdit, canAdd]);



    const renderEventDetail = (event) => {
        if (!event) return null;
        const isSuccess = event.Status?.toLowerCase() === 'success';
        let profile = {};
        try { profile = JSON.parse(event.EntityProfile || '{}'); } catch (e) { }

        return (
            <Box sx={{ p: 4, bgcolor: 'white', height: '100%', overflowY: 'auto' }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 4 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        <Avatar src="/assets/custom/img/img_avatar1.png" sx={{ width: 64, height: 64 }} />
                        <Box>
                            <Typography variant="h5" sx={{ fontWeight: 800, color: '#1e293b' }}>
                                {profile.PersonName || 'No Profile'}
                            </Typography>
                            <Typography variant="body2" sx={{ color: '#64748b', fontWeight: 600 }}>
                                {profile.EntiyName || 'N/A'} | {profile.EntiyName?.toLowerCase() === 'lead' ? profile.LeadNo : profile.EntityId}
                            </Typography>
                        </Box>
                    </Box>
                    <Chip
                        label={event.Status}
                        sx={{
                            bgcolor: isSuccess ? '#ecfdf5' : '#fff7ed',
                            color: isSuccess ? '#10b981' : '#f97316',
                            fontWeight: 800,
                            px: 1
                        }}
                    />
                </Box>

                <Grid container spacing={4} sx={{ mb: 4 }}>
                    <Grid item xs={6}>
                        <Typography variant="caption" sx={{ color: '#94a3b8', fontWeight: 700, textTransform: 'uppercase' }}>Created On</Typography>
                        <Typography variant="body1" sx={{ color: '#334155', fontWeight: 600, mt: 0.5 }}>{event.CreatedOn}</Typography>
                    </Grid>
                    <Grid item xs={6}>
                        <Typography variant="caption" sx={{ color: '#94a3b8', fontWeight: 700, textTransform: 'uppercase' }}>Request ID</Typography>
                        <Typography variant="body1" sx={{ color: '#334155', fontWeight: 600, mt: 0.5 }}>{event.Id}</Typography>
                    </Grid>
                </Grid>

                <Divider sx={{ my: 4 }} />

                <Typography variant="subtitle2" sx={{ fontWeight: 800, color: '#1e293b', mb: 2, textTransform: 'uppercase' }}>Event Execution Details</Typography>
                <Box sx={{ p: 3, bgcolor: '#f8fafc', borderRadius: '12px', border: '1px solid #e2e8f0', mb: 4 }}>
                    {isSuccess ? (
                        <Typography variant="body2" sx={{ color: '#475569', lineHeight: 1.6 }}>{event.EventDescription}</Typography>
                    ) : (
                        <Box>
                            <Typography variant="body2" sx={{ color: '#ef4444', mb: 2 }}>{event.ErrorDescription || 'Pending generation'}</Typography>
                            {(canEdit || canAdd) && (
                                <button
                                    className="btn btn-primary btn-sm"
                                    onClick={() => handleGenerateEvent(event.Id, event.RequestJson)}
                                    style={{ borderRadius: '6px', fontWeight: 600 }}
                                >
                                    Generate Event Now
                                </button>
                            )}
                        </Box>
                    )}
                </Box>

                <Typography variant="subtitle2" sx={{ fontWeight: 800, color: '#1e293b', mb: 2, textTransform: 'uppercase' }}>Raw JSON Payload</Typography>
                <Box sx={{
                    p: 3,
                    bgcolor: '#1e293b',
                    color: '#e2e8f0',
                    borderRadius: '12px',
                    fontFamily: 'monospace',
                    fontSize: '13px',
                    maxHeight: '400px',
                    overflowY: 'auto',
                    position: 'relative'
                }}>
                    <IconButton
                        size="small"
                        onClick={() => {
                            navigator.clipboard.writeText(event.RequestJson);
                            Swal.fire('Copied', 'JSON copied to clipboard', 'success');
                        }}
                        sx={{ position: 'absolute', top: 8, right: 8, color: '#94a3b8', '&:hover': { color: 'white' } }}
                    >
                        <Icon icon="ri:file-copy-line" />
                    </IconButton>
                    <pre style={{ margin: 0, whiteSpace: 'pre-wrap' }}>
                        {JSON.stringify(JSON.parse(event.RequestJson || '{}'), null, 2)}
                    </pre>
                </Box>
            </Box>
        );
    };

    const customCards = [
        {
            title: stats.total || 0,
            subtitle: 'Total Logs',
            icon: 'ri:history-line',
            color: 'primary',
            key: 'All'
        },
        {
            title: stats.success || 0,
            subtitle: 'Success',
            icon: 'ri:checkbox-circle-line',
            color: 'success',
            key: 'Success'
        },
        {
            title: stats.pending || 0,
            subtitle: 'Pending',
            icon: 'ri:time-line',
            color: 'warning',
            key: 'Pending'
        },
        {
            title: stats.failed || 0,
            subtitle: 'Failed',
            icon: 'ri:error-warning-line',
            color: 'error',
            key: 'Failed'
        }
    ];

    const renderRequestJsonDrawer = () => {
        let parsed = {};
        try {
            parsed = JSON.parse(drawerJsonStr || '{}');
        } catch (e) {
            console.error('Error parsing drawer JSON:', e);
        }

        const fields = Object.entries(parsed).filter(([key]) => {
            return key !== 'EventIdentifier' && key !== 'EntityIdentifier';
        });

        const isEditMode = drawerMode === 'edit';

        return (
            <Drawer
                anchor="right"
                open={isDrawerOpen}
                onClose={() => setIsDrawerOpen(false)}
                PaperProps={{
                    sx: { width: { xs: '100%', sm: 600 }, borderLeft: '1px solid #e2e8f0' }
                }}
            >
                {/* Header */}
                <Box sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1.5,
                    p: 2.5,
                    bgcolor: '#f8fafc',
                    borderBottom: '1px solid #e2e8f0',
                    cursor: 'pointer'
                }} onClick={() => setIsDrawerOpen(false)}>
                    <Icon icon="ri:close-line" fontSize={20} color="#475569" />
                    <Typography sx={{ fontWeight: 600, color: '#475569', fontSize: '14px' }}>
                        Back to Event Details List
                    </Typography>
                </Box>

                {/* Content */}
                <Box sx={{ p: 4, flex: 1, overflowY: 'auto' }}>
                    <Typography variant="h6" sx={{ fontWeight: 800, color: '#1e293b', mb: 3 }}>
                        {isEditMode ? 'Event Details' : 'Event Details'}
                    </Typography>

                    {/* Raw JSON String Display Box */}
                    {!isEditMode && (
                        <Box sx={{
                            p: 2.5,
                            border: '1px solid #e2e8f0',
                            borderRadius: '8px',
                            bgcolor: 'white',
                            fontFamily: 'monospace',
                            fontSize: '12px',
                            color: '#334155',
                            wordBreak: 'break-all',
                            mb: 4,
                            lineHeight: 1.5
                        }}>
                            {drawerJsonStr}
                        </Box>
                    )}

                    {/* Fields List */}
                    {fields.length > 0 ? (
                        fields.map(([key, value]) => {
                            const displayValue = Array.isArray(value)
                                ? value.join(',')
                                : typeof value === 'object' && value !== null
                                    ? JSON.stringify(value)
                                    : String(value);

                            if (isEditMode) {
                                return (
                                    <Box key={key} sx={{ mb: 4 }}>
                                        <Typography sx={{ fontWeight: 700, fontSize: '13px', color: '#1e293b', mb: 1 }}>
                                            {key}
                                        </Typography>
                                        <Grid container spacing={2} alignItems="center">
                                            {/* Left Side: Original Value */}
                                            <Grid item xs={12} sm={5.5}>
                                                <Box sx={{
                                                    bgcolor: '#f1f5f9',
                                                    border: '1px solid #e2e8f0',
                                                    borderRadius: '8px',
                                                    p: 1.8,
                                                    fontSize: '13px',
                                                    color: '#475569',
                                                    minHeight: '38px',
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    userSelect: 'none'
                                                }}>
                                                    {displayValue}
                                                </Box>
                                            </Grid>

                                            {/* Middle: Copy Button */}
                                            <Grid item xs={12} sm={1} sx={{ display: 'flex', justifyContent: 'center' }}>
                                                <Tooltip title="Copy original value">
                                                    <IconButton
                                                        size="small"
                                                        onClick={() => handleCopyValue(key, displayValue)}
                                                        sx={{
                                                            border: '1px solid #cbd5e1',
                                                            borderRadius: '6px',
                                                            bgcolor: 'white',
                                                            '&:hover': { bgcolor: '#f1f5f9' }
                                                        }}
                                                    >
                                                        <Icon icon="ri:arrow-right-line" fontSize={18} color="#94a3b8" />
                                                    </IconButton>
                                                </Tooltip>
                                            </Grid>

                                            {/* Right Side: Editable Value */}
                                            <Grid item xs={12} sm={5.5}>
                                                <input
                                                    type="text"
                                                    className="form-control"
                                                    value={editFields[key] ?? ''}
                                                    onChange={(e) => handleEditFieldChange(key, e.target.value)}
                                                    style={{
                                                        borderRadius: '8px',
                                                        padding: '8px 12px',
                                                        fontSize: '13px',
                                                        border: '1px solid #cbd5e1',
                                                        width: '100%',
                                                        backgroundColor: 'white',
                                                        color: '#334155'
                                                    }}
                                                    placeholder="Enter value"
                                                />
                                            </Grid>
                                        </Grid>
                                    </Box>
                                );
                            }

                            return (
                                <Box key={key} sx={{ mb: 4 }}>
                                    <Typography sx={{ fontWeight: 700, fontSize: '13px', color: '#1e293b', mb: 1 }}>
                                        {key}
                                    </Typography>

                                    {/* Top Box */}
                                    <Box sx={{
                                        bgcolor: '#f1f5f9',
                                        border: '1px solid #e2e8f0',
                                        borderRadius: '8px',
                                        p: 1.8,
                                        fontSize: '13px',
                                        color: '#475569',
                                        minHeight: '38px',
                                        display: 'flex',
                                        alignItems: 'center'
                                    }}>
                                        {displayValue}
                                    </Box>

                                    {/* Connector Bar with Arrow Icon */}
                                    <Box sx={{
                                        my: 1.5,
                                        height: '34px',
                                        bgcolor: '#f1f5f9',
                                        borderRadius: '6px',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        border: '1px solid #e2e8f0'
                                    }}>
                                        <Icon icon="ri:arrow-right-line" fontSize={18} color="#94a3b8" />
                                    </Box>

                                    {/* Bottom Box */}
                                    <Box sx={{
                                        bgcolor: '#f1f5f9',
                                        border: '1px solid #e2e8f0',
                                        borderRadius: '8px',
                                        p: 1.8,
                                        fontSize: '13px',
                                        color: '#475569',
                                        minHeight: '38px',
                                        display: 'flex',
                                        alignItems: 'center'
                                    }}>
                                        {displayValue}
                                    </Box>
                                </Box>
                            );
                        })
                    ) : (
                        <Typography sx={{ color: '#64748b', fontSize: '14px', fontStyle: 'italic' }}>
                            No event fields to display
                        </Typography>
                    )}
                </Box>

                {/* Footer */}
                <Box sx={{
                    p: 3,
                    bgcolor: '#f8fafc',
                    borderTop: '1px solid #e2e8f0',
                    display: 'flex',
                    justifyContent: 'flex-start',
                    gap: 2
                }}>
                    {isEditMode ? (
                        <>
                            {canEdit && (
                                <button
                                    className="btn btn-primary"
                                    onClick={handleSaveEvent}
                                    style={{
                                        borderRadius: '6px',
                                        fontWeight: 600,
                                        padding: '6px 20px',
                                        fontSize: '14px'
                                    }}
                                >
                                    Save
                                </button>
                            )}
                            <button
                                className="btn btn-outline-secondary"
                                onClick={() => setIsDrawerOpen(false)}
                                style={{
                                    borderRadius: '6px',
                                    fontWeight: 600,
                                    padding: '6px 20px',
                                    border: '1px solid #cbd5e1',
                                    color: '#475569',
                                    fontSize: '14px',
                                    backgroundColor: 'white'
                                }}
                            >
                                Cancel
                            </button>
                        </>
                    ) : (
                        <button
                            className="btn btn-outline-secondary"
                            onClick={() => setIsDrawerOpen(false)}
                            style={{
                                borderRadius: '6px',
                                fontWeight: 600,
                                padding: '6px 20px',
                                border: '1px solid #cbd5e1',
                                color: '#475569',
                                fontSize: '14px',
                                backgroundColor: 'white'
                            }}
                        >
                            Cancel
                        </button>
                    )}
                </Box>
            </Drawer>
        );
    };

    if (!canView) {
        return (
            <Box sx={{ display: 'flex', flexDirection: 'column', height: '100vh', bgcolor: '#f1f5f9' }}>
                <input type="hidden" id="hdnModuleCode" name="hdnModuleCode" value="PAG10332" readOnly />
                <PermissionDeniedComponent />
            </Box>
        );
    }

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', height: '100vh', bgcolor: '#f1f5f9' }}>
            <input type="hidden" id="hdnModuleCode" name="hdnModuleCode" value="PAG10332" readOnly />
            <SubHeader
                title="Webhook Monitoring"
                searchQuery={searchTerm}
                setSearchQuery={setSearchTerm}
                pageName="Webook Events"
                pageCode="PAG10332"
                searchPlaceholder="Search events..."
                onSearchClick={canSearch ? fetchEvents : undefined}
                viewMode={viewMode}
                setViewMode={setViewMode}
                showViewSwitcher={true}
                showGrid={true}
                showTable={true}
                showCalendar={false}
                showSearch={canSearch}
                totalCount={totalRecords}
                onRefreshClick={fetchEvents}
                showRefresh={true}
            // onAddClick={() => navigate('/custom-events/create')}
            >
                <IconButton
                    onClick={handleShowAll}
                    title="Show All / Refresh"
                    sx={{
                        ml: 1,
                        border: '1px solid #e2e8f0',
                        borderRadius: '8px',
                        width: '40px',
                        height: '40px',
                        color: '#64748b',
                        '&:hover': { backgroundColor: '#f1f5f9' }
                    }}
                >
                    <Icon icon="ri:refresh-line" fontSize={20} />
                </IconButton>
            </SubHeader>

            {viewMode === 'table' ? (
                <PremiumModuleWrapper
                    moduleName="Log"
                    stats={stats}
                    customCards={customCards}
                    data={data}
                    columns={columns}
                    totalRecords={totalRecords}
                    currentPage={currentPage}
                    itemsPerPage={itemsPerPage}
                    onPageChange={setCurrentPage}
                    onItemsPerPageChange={setItemsPerPage}
                    loading={loading}
                    hasFetched={hasFetched}
                    onStatusClick={(status) => status === 'ResetAll' && handleShowAll()}
                    px="24px"
                />
            ) : (
                <Box sx={{ flex: 1, overflow: 'hidden' }}>
                    <GridMainPanelLayout
                        type="event"
                        hideSidebarHeader={true}
                        showSelection={false}
                        data={data.map(item => {
                            let profile = {};
                            try { profile = JSON.parse(item.EntityProfile || '{}'); } catch (e) { }
                            return {
                                ...item,
                                id: item.Id,
                                title: profile.PersonName || 'No Profile',
                                name: item.CreatedOn,
                                status: item.Status,
                                _badgeText: item.Status?.toUpperCase(),
                                _statusClass: item.Status?.toLowerCase() === 'success' ? 'completed' : 'overdue'
                            };
                        })}
                        loading={loading}
                        totalRecords={totalRecords}
                        currentPage={currentPage}
                        totalPages={Math.ceil(totalRecords / itemsPerPage)}
                        onPageChange={setCurrentPage}
                        selectedItem={selectedItem}
                        onSelectItem={setSelectedItem}
                        renderDetailContent={renderEventDetail}
                        formatDate={(d) => d}
                        getInitials={(n) => n ? n.charAt(0).toUpperCase() : 'W'}
                    />
                </Box>
            )}
            {renderRequestJsonDrawer()}
        </Box>
    );
};

export default WebhookEventsWrapper;
